import { coerceTimestampToDate } from './system-metadata.util';

/** UI-ready audit fields from the four audit keys; `__system` wins when set, else same key on the row root. */
export interface CompanyAuditDisplay {
  /** Human-readable label for the audited record (e.g. company name). */
  recordLabel?: string;
  createdByName: string;
  createdByAvatarUrl?: string;
  /** When name is generic, hash this for avatar color (e.g. user id). */
  createdByColorSeed?: string;
  createdOnDisplay: string;
  modifiedByName: string;
  modifiedByAvatarUrl?: string;
  modifiedByColorSeed?: string;
  modifiedOnDisplay: string;
}

/** Viewport anchor for positioning the audit popover near the trigger. */
export interface AuditPopoverAnchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

function formatAuditDateTime(value: Date | string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function extractAvatarUrl(obj: Record<string, unknown> | null | undefined): string | undefined {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  const profile = obj['profile'] as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    obj['profileImage'],
    obj['avatar'],
    obj['photoURL'],
    obj['photoUrl'],
    obj['profilePicture'],
    obj['imageUrl'],
    profile?.['imageUrl'],
    profile?.['photoURL'],
    profile?.['avatar'],
    profile?.['profileImage'],
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) {
      return c.trim();
    }
  }
  return undefined;
}

const AUDIT_USER_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Opaque user id in audit fields — Firebase-style UID or SQL/Supabase UUID.
 * Not a display name; resolve via user directory / GET /users/:id.
 */
export function isAuditUserId(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  const s = value.trim();
  if (!s || s.includes('@') || s.includes(' ')) {
    return false;
  }
  if (/^[a-zA-Z0-9]{20,}$/.test(s)) {
    return true;
  }
  return AUDIT_USER_UUID_RE.test(s);
}

/** @deprecated Use isAuditUserId — kept for existing imports. */
export function isBareFirebaseUid(value: unknown): value is string {
  return isAuditUserId(value);
}

/**
 * If the value is a bare user id (e.g. Firebase UID), treat as unknown display name.
 * If it is a readable string (name, email), use it.
 */
function extractDisplayName(field: unknown): string | undefined {
  if (field == null) {
    return undefined;
  }
  if (typeof field === 'string') {
    const s = field.trim();
    if (!s) {
      return undefined;
    }
    if (isAuditUserId(s)) {
      return undefined;
    }
    return s;
  }
  if (typeof field === 'object') {
    const o = field as Record<string, unknown>;
    const n =
      o['name'] ?? o['displayName'] ?? o['fullName'] ?? o['email'];
    if (typeof n === 'string' && n.trim()) {
      const t = n.trim();
      if (isAuditUserId(t)) {
        return undefined;
      }
      return t;
    }
  }
  return undefined;
}

function isMissingAuditUserRef(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return false;
}

/**
 * `__system.createdBy` / `__system.updatedBy` when present; otherwise top-level `createdBy` / `updatedBy` only.
 */
function pickUserLike(row: Record<string, unknown> | null | undefined, role: 'created' | 'modified'): unknown {
  if (!row) {
    return undefined;
  }
  const key = role === 'created' ? 'createdBy' : 'updatedBy';
  const sys = row['__system'] as Record<string, unknown> | undefined;
  if (sys && typeof sys === 'object') {
    const fromSys = sys[key];
    if (!isMissingAuditUserRef(fromSys)) {
      return fromSys;
    }
  }
  return row[key];
}

const AUDIT_USER_KEYS = ['createdBy', 'updatedBy'] as const;

function collectUidsFromAuditRef(value: unknown, into: Set<string>): void {
  if (isAuditUserId(value)) {
    into.add(value.trim());
    return;
  }
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    for (const k of ['uid', 'id', 'userId'] as const) {
      const x = o[k];
      if (isAuditUserId(x)) {
        into.add((x as string).trim());
      }
    }
  }
}

/** Distinct bare UIDs on `createdBy` / `updatedBy` (top-level and `__system`) for `/users/:id` lookups. */
export function collectBareAuditUserIds(row: unknown): string[] {
  if (row == null || typeof row !== 'object') {
    return [];
  }
  const r = row as Record<string, unknown>;
  const ids = new Set<string>();
  collectUidsFromAuditRef(r['createdBy'], ids);
  collectUidsFromAuditRef(r['updatedBy'], ids);
  const sys = r['__system'] as Record<string, unknown> | undefined;
  if (sys && typeof sys === 'object') {
    collectUidsFromAuditRef(sys['createdBy'], ids);
    collectUidsFromAuditRef(sys['updatedBy'], ids);
  }
  return [...ids];
}

/** Prefer `__system` timestamp; if missing or unparseable, use top-level `createdAt` / `updatedAt`. */
function getAuditTimestamp(
  row: Record<string, unknown> | null | undefined,
  key: 'createdAt' | 'updatedAt'
): Date | undefined {
  if (!row) {
    return undefined;
  }
  const sys = row['__system'] as Record<string, unknown> | undefined;
  if (sys && typeof sys === 'object') {
    const fromSys = coerceTimestampToDate(sys[key]);
    if (fromSys) {
      return fromSys;
    }
  }
  return coerceTimestampToDate(row[key]);
}

/**
 * Shallow clone; replaces bare UIDs on `__system` and top-level `createdBy` / `updatedBy` (same keys only).
 */
export function applyAuditUserNamesLookup(
  row: unknown,
  lookup: Readonly<Record<string, string>> | null | undefined
): unknown {
  if (row == null || typeof row !== 'object') {
    return row;
  }
  if (!lookup || Object.keys(lookup).length === 0) {
    return row;
  }
  const r = row as Record<string, unknown>;
  const sysIn = r['__system'];
  const sysClone =
    sysIn && typeof sysIn === 'object'
      ? { ...(sysIn as Record<string, unknown>) }
      : undefined;

  const top = { ...r } as Record<string, unknown>;
  if (sysClone) {
    top['__system'] = sysClone;
  }

  const replace = (container: Record<string, unknown>) => {
    for (const key of AUDIT_USER_KEYS) {
      const v = container[key];
      if (isAuditUserId(v)) {
        const name = lookup[v.trim()];
        if (name?.trim()) {
          container[key] = name.trim();
        }
      } else if (v && typeof v === 'object') {
        const o = v as Record<string, unknown>;
        for (const uk of ['uid', 'id', 'userId'] as const) {
          const uid = o[uk];
          if (isAuditUserId(uid)) {
            const name = lookup[(uid as string).trim()];
            if (name?.trim()) {
              container[key] = name.trim();
            }
            break;
          }
        }
      }
    }
  };

  replace(top);
  if (sysClone) {
    replace(sysClone);
  }

  return top;
}

/** Build uid/id → display name from users already loaded for a list (e.g. users page or directory prefetch). */
export function buildAuditUserNamesLookupFromUsers(
  users: ReadonlyArray<{ id?: string; uid?: string; name?: string | null } | null | undefined> | null | undefined
): Record<string, string> {
  const m: Record<string, string> = {};
  if (!users?.length) {
    return m;
  }
  for (const u of users) {
    if (!u) {
      continue;
    }
    const name = typeof u.name === 'string' ? u.name.trim() : '';
    if (!name) {
      continue;
    }
    const id = u.id?.trim();
    const uid = u.uid?.trim();
    if (id) {
      m[id] = name;
    }
    if (uid) {
      m[uid] = name;
    }
  }
  return m;
}

function resolveNameAndAvatar(userLike: unknown): {
  name: string;
  avatar?: string;
  colorSeed?: string;
} {
  if (userLike == null) {
    return { name: 'Unknown User' };
  }
  if (typeof userLike === 'object') {
    const o = userLike as Record<string, unknown>;
    const avatar = extractAvatarUrl(o);
    const displayName = extractDisplayName(userLike);
    if (displayName) {
      return { name: displayName, avatar };
    }
    const uidLike = o['uid'] ?? o['id'] ?? o['userId'];
    if (typeof uidLike === 'string') {
      const t = uidLike.trim();
      if (isAuditUserId(t)) {
        return { name: 'Unknown User', avatar, colorSeed: t };
      }
    }
    return { name: 'Unknown User', avatar };
  }
  if (typeof userLike === 'string') {
    const s = userLike.trim();
    if (isAuditUserId(s)) {
      return { name: 'Unknown User', colorSeed: s };
    }
    const name = extractDisplayName(userLike) ?? 'Unknown User';
    return { name };
  }
  const name = extractDisplayName(userLike) ?? 'Unknown User';
  return { name };
}

/** Best-effort display label for the record being audited. */
export function extractAuditRecordLabel(row: unknown): string | undefined {
  if (row == null || typeof row !== 'object') {
    return undefined;
  }
  const r = row as Record<string, unknown>;
  const candidates = [
    'name',
    'title',
    'invoiceNumber',
    'purchaseOrderNumber',
    'deliveryNoteNumber',
    'code',
    'sku',
    'email',
    'roleName',
    'productName',
  ];
  for (const key of candidates) {
    const value = r[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/**
 * Normalize a list row into audit display fields.
 * Uses only `createdBy` / `updatedBy` / `createdAt` / `updatedAt`, preferring `__system` then top-level.
 * Optional `userNamesById` resolves bare UIDs on those two user keys (in `__system` and/or top-level).
 */
export function normalizeAuditFromRow(
  row: unknown,
  userNamesById?: Readonly<Record<string, string>> | null
): CompanyAuditDisplay {
  const source = applyAuditUserNamesLookup(row, userNamesById ?? undefined);
  const r = source as Record<string, unknown> | null | undefined;
  const created = resolveNameAndAvatar(pickUserLike(r, 'created'));
  const modified = resolveNameAndAvatar(pickUserLike(r, 'modified'));

  const createdAt = getAuditTimestamp(r, 'createdAt');
  const updatedAt = getAuditTimestamp(r, 'updatedAt');

  return {
    recordLabel: extractAuditRecordLabel(row),
    createdByName: created.name || 'Unknown User',
    createdByAvatarUrl: created.avatar,
    createdByColorSeed: created.colorSeed,
    createdOnDisplay: formatAuditDateTime(createdAt),
    modifiedByName: modified.name || 'Unknown User',
    modifiedByAvatarUrl: modified.avatar,
    modifiedByColorSeed: modified.colorSeed,
    modifiedOnDisplay: formatAuditDateTime(updatedAt),
  };
}

/** @deprecated Use normalizeAuditFromRow — same behavior */
export function normalizeCompanyAuditFromCompanyRow(
  company: unknown
): CompanyAuditDisplay {
  return normalizeAuditFromRow(company);
}

/** Alias for use in generic list pages */
export type AuditInfoDisplay = CompanyAuditDisplay;
